import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { checkLoginStatus } from 'auth/actions';
import { getStore } from 'global/store';
import { ROUTES } from '../../../global/constants';
import { NavLink, useHistory } from 'react-router-dom';
import { Icon } from '../../../elements';
import { useColor } from '../../ThemeProvider';
import classNames from 'classnames';
import { WrappedMessage } from '../../../utils';
import { getUserCareerProfile, getUsername } from '../../../auth/selectors';
import messages from '../../displayMessages';
import { CareerSearchResult, RelatedCareer } from 'labxchange-client';
import { CareersApi } from 'global/api';
import { intl } from 'i18n';
import { ShareModal } from 'ui/components';
import PulseCard from '../PulseCard';

interface CareerCardProps {
  career: CareerSearchResult | RelatedCareer;
  onClick?: () => void;
  showImage?: boolean;
}

export const formatSalary = (salary: number): string => {
  const formattedSalary =
    salary >= 1000 ? `${salary / 1000}k` : salary?.toString();
  const entryLevelText = intl.formatMessage(messages.careerCardAnnualSalary);
  return `$${formattedSalary} ${entryLevelText}`;
};

const CareerCard: React.FC<CareerCardProps> = ({ career, showImage }) => {
  const { selectedColor } = useColor();
  const history = useHistory();
  const loggedInUsername = useSelector(getUsername);
  const careers = useSelector(getUserCareerProfile)?.careers || [];
  const [isSaved, setIsSaved] = useState(
    careers.some((savedCareer) => savedCareer.slug === career.slug)
  );
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const onSaveCareer = async (id: number) => {
    await CareersApi.profilePartialUpdate({
      id: loggedInUsername,
      data: { savedCareerId: id },
    }).then(() => {
      setIsSaved(!isSaved);
      checkLoginStatus(getStore().dispatch);
    });
  };

  return (
    <>
      <div
        className={classNames('career-card', {
          'career-card__list-view': showImage,
          [selectedColor.class]: !showImage,
        })}
        data-testid='career-card'
      >
        <NavLink
          to={ROUTES.CareerExplorer.CAREER_DETAILS_SLUG(career.slug)}
          className='career-card__link'
        >
          <div className='career-card__title-section'>
            <h5>{career.title}</h5>
          </div>

          {showImage && (
            <img
              className='career-card__career-image'
              alt='Career Image'
              src={
                career.icon ||
                '/assets/images/career-explorer/career-intro-image.svg'
              }
            />
          )}
          {!showImage && (
            <div
              className={`career-card__icon-section ${selectedColor.class}`}
              data-testid='career-card__icon-section'
            >
              <Icon name='arrow-right' fill={selectedColor.iconColorFill} />
            </div>
          )}
          <div className='career-card__content'>
            <div className='career-card__content__item'>
              <Icon
                className='career-card__content__item__icon'
                name={'education'}
                fill='#3E5DB3'
              />
              <span className='career-card__content__item__text'>
                {career.minimumQualification?.careerCardInformation}
              </span>
            </div>

            <div className='career-card__content__item'>
              <Icon
                className='career-card__content__item__icon'
                name={'salary'}
                fill='#3E5DB3'
              />
              <span className='career-card__content__item__text'>
                {' '}
                {formatSalary(career.entryLevelSalary)}{' '}
              </span>
            </div>
          </div>
        </NavLink>

        <div className='career-card__divider'></div>
        <div className='career-card__footer'>
          <button
            className='career-card__footer__action-button career-card__footer__bottom-left'
            type='button'
            onClick={(e) => {
              onSaveCareer(career.id);
            }}
          >
            <>
              <Icon
                name={isSaved ? 'bookmark-filled' : 'bookmark'}
                fill='#003E6B'
              />
              <span>
                {' '}
                {isSaved ? (
                  <WrappedMessage message={messages.careerSavedAction} />
                ) : (
                  <WrappedMessage message={messages.careerSaveAction} />
                )}{' '}
              </span>
            </>
          </button>
          <div className='career-card__footer__button-divider'></div>
          <button
            className='career-card__footer__action-button career-card__footer__bottom-right'
            type='button'
            onClick={(e) => {
              setIsShareModalOpen(true);
            }}
          >
            <>
              <Icon name={'share-careerexplorer'} fill='#003E6B' />
              <span>
                {' '}
                <WrappedMessage message={messages.careerShareAction} />{' '}
              </span>
            </>
          </button>
        </div>
      </div>

      {isShareModalOpen && (
        <ShareModal
          onClose={() => {
            setIsShareModalOpen(false);
          }}
          getItemUrl={() =>
            ROUTES.CareerExplorer.CAREER_DETAILS_SLUG(career.slug)
          }
          itemTitle={intl.formatMessage(messages.careerShareMetaTitle)}
          itemDescription={intl.formatMessage(
            messages.careerShareMetaDescription
          )}
          itemImageUrl={career.icon}
          variant='career-explorer'
        />
      )}
    </>
  );
};

export default CareerCard;
